//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Add } from '@carbon/icons-react';
import { Button } from '../ui/button';
import GroupsTable from './GroupsTable';
import AddGroupSidebar from './AddGroupSidebar';
import DeleteGroupModal from './DeleteGroupModal';
import { UserGroupDto, GroupFormData, IntegrationOption } from './types';
import UserGroupDataService from '../../utils/UserGroupDataService';
import { useConfig } from '../../Config';
import { useUserAuth } from '../../UserAuth';
import { useTeamspaceContext } from '../../TeamspaceContext';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';

const GroupsPage: React.FC = () => {
  const { t } = useTranslation();
  const { services } = useConfig();
  const { user } = useUserAuth();
  const { teamspaceId, teamspaceUsers } = useTeamspaceContext();
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const [groups, setGroups] = useState<UserGroupDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupFormData | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<IntegrationOption[]>([]);
  const [users, setUsers] = useState<
    Array<{ id: string; name: string; email: string }>
  >([]);
  const [currentUserDbId, setCurrentUserDbId] = useState<string>('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const tenantBaseUrl = services?.tenant?.url ?? '';

  useEffect(() => {
    if (!teamspaceId || !tenantBaseUrl) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const dataService = UserGroupDataService.getInstance(
          tenantBaseUrl,
          () => user,
        );

        // Fetch all data in parallel
        const [fetchedGroups, integrationsData, usersData] = await Promise.all([
          dataService.listGroups(teamspaceId),
          dataService.listTeamspaceIntegrations(teamspaceId),
          dataService.listTeamspaceUsers(teamspaceId),
        ]);

        setGroups(fetchedGroups);

        // Transform integrations data to IntegrationOption format
        const integrationOptions: IntegrationOption[] = integrationsData.map(
          (int: any) => ({
            id: int.id || int.name, // Use id if available, fallback to name
            name: int.name,
            teamspaceId,
          }),
        );
        setIntegrations(integrationOptions);

        // Transform users data to match expected format
        const userOptions = usersData.map((u) => ({
          id: u.id,
          name: u.fullname || u.username,
          email: u.username, // Using username as email placeholder
        }));
        setUsers(userOptions);

        // Find current user's database ID by matching keycloakId with OIDC sub
        const currentKeycloakId = user?.profile?.sub;
        if (currentKeycloakId) {
          const currentUser = usersData.find(
            (u) => u.keycloakId === currentKeycloakId,
          );
          if (currentUser) {
            setCurrentUserDbId(currentUser.id);
          }
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || t('userGroups.errors.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [teamspaceId, tenantBaseUrl, user?.profile?.sub, t, teamspaceUsers]);

  const handleEdit = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (group) {
      // Find the integration ID from the integration name
      const assignedIntegration = integrations.find(
        (int) => int.name === group.integrationName,
      );

      setEditingGroupId(groupId);
      setEditingGroup({
        name: group.name,
        description: group.description || '',
        selectedIntegrationId: assignedIntegration?.id || '',
        allowUserOverride: group.allowUserOverride,
        selectedUserIds: group.userIds,
      });
      setIsSidebarOpen(true);
    }
  };

  const handleDelete = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (group) {
      setGroupToDelete({ id: group.id, name: group.name });
      setDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!teamspaceId || !groupToDelete) {
      return;
    }

    const currentTeamspaceId = teamspaceId;

    try {
      const dataService = UserGroupDataService.getInstance(
        tenantBaseUrl,
        () => user,
      );

      await dataService.deleteGroup(currentTeamspaceId, groupToDelete.id);

      // Refresh groups list
      const fetchedGroups = await dataService.listGroups(currentTeamspaceId);
      setGroups(fetchedGroups);
      setDeleteModalOpen(false);
      setGroupToDelete(null);

      // Show success notification
      showSnackbar(t('userGroups.success.deleted'), 'success');
    } catch (err: any) {
      console.error('Error deleting group:', err);
      throw err;
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setGroupToDelete(null);
  };

  const handleAddGroup = () => {
    setEditingGroup(null);
    setEditingGroupId(null);
    setIsSidebarOpen(true);
  };

  const handleSaveGroup = async (formData: GroupFormData) => {
    if (!teamspaceId) {
      throw new Error('Teamspace ID is required');
    }

    const currentTeamspaceId = teamspaceId;

    try {
      const dataService = UserGroupDataService.getInstance(
        tenantBaseUrl,
        () => user,
      );

      let groupId: string;

      if (editingGroupId) {
        // Update existing group (name is immutable, cannot be changed)
        await dataService.updateGroup(currentTeamspaceId, editingGroupId, {
          description: formData.description,
          userIds: formData.selectedUserIds,
        });
        groupId = editingGroupId;
      } else {
        // Create new group
        const createdGroup = await dataService.createGroup(currentTeamspaceId, {
          name: formData.name,
          description: formData.description,
          allowUserOverride: formData.allowUserOverride,
          userIds: formData.selectedUserIds,
        });
        groupId = createdGroup.id;
      }

      // Handle integration assignment if selected
      // Skip API call if integration hasn't changed (optimization)
      if (formData.selectedIntegrationId) {
        const selectedIntegration = integrations.find(
          (int) => int.id === formData.selectedIntegrationId,
        );

        // Derive previous integration from live groups state
        const currentGroup = groups.find((g) => g.id === editingGroupId);
        const previousIntegrationName = currentGroup?.integrationName;

        // Check if integration actually changed
        const integrationChanged =
          !editingGroupId || // New group - always assign
          !previousIntegrationName || // No previous integration
          selectedIntegration?.name !== previousIntegrationName; // Different integration

        if (selectedIntegration && integrationChanged) {
          await dataService.assignIntegrationToGroup(
            currentTeamspaceId,
            selectedIntegration.name,
            groupId,
          );
        }
      }

      // Refresh groups list
      const fetchedGroups = await dataService.listGroups(currentTeamspaceId);
      setGroups(fetchedGroups);
      setIsSidebarOpen(false);
      setEditingGroupId(null);

      // Show success notification
      const successMessage = editingGroupId
        ? t('userGroups.success.updated')
        : t('userGroups.success.created');
      showSnackbar(successMessage, 'success');
    } catch (err: any) {
      console.error('Error saving group:', err);
      throw err;
    }
  };

  const handleToggleOverride = async (groupId: string, newValue: boolean) => {
    if (!teamspaceId) {
      throw new Error('Teamspace ID is required');
    }

    try {
      const dataService = UserGroupDataService.getInstance(
        tenantBaseUrl,
        () => user,
      );

      await dataService.updateOverridePolicy(teamspaceId, groupId, newValue);

      // Update local state
      setGroups((prevGroups) =>
        prevGroups.map((g) =>
          g.id === groupId ? { ...g, allowUserOverride: newValue } : g,
        ),
      );
    } catch (err: any) {
      console.error('Error toggling override policy:', err);
      throw err;
    }
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setEditingGroup(null);
    setEditingGroupId(null);
  };

  return (
    <div
      data-testid="groups-page"
      className="min-h-screen bg-[#F4F4F4] p-6 dark:bg-[#161616]"
    >
      <div className="container mx-auto">
        {/* White Card Container */}
        <div className="rounded-lg bg-white p-6 dark:bg-[#262626]">
          {/* Header with All Groups title and Add Button */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-medium text-[#4a4a56] dark:text-[#c8c8dd]">
                {t('userGroups.allGroups')}
              </h2>
              <span className="rounded-full bg-[#3c91ff] px-2 py-0.5 text-xs font-semibold text-white">
                {groups.length}
              </span>
            </div>
            <Button
              onClick={handleAddGroup}
              data-testid="groups-page-add-button"
              className="flex items-center gap-2"
            >
              <Add size={20} />
              {t('userGroups.addGroup')}
            </Button>
          </div>

          {/* Error State */}
          {error && (
            <div
              data-testid="groups-page-error"
              className="mb-4 rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {error}
            </div>
          )}

          {/* Groups Table */}
          <GroupsTable
            groups={groups}
            loading={loading}
            currentUserId={currentUserDbId}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleOverride={handleToggleOverride}
          />
        </div>
      </div>

      {/* Add/Edit Group Sidebar */}
      <AddGroupSidebar
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
        onSave={handleSaveGroup}
        integrations={integrations}
        users={users}
        allGroups={groups}
        editingGroupId={editingGroupId}
        editGroup={editingGroup}
      />

      {/* Delete Group Modal */}
      <DeleteGroupModal
        isOpen={deleteModalOpen}
        groupName={groupToDelete?.name || ''}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />

      {/* Success/Error Notifications */}
      <CustomSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={closeSnackbar}
      />
    </div>
  );
};

export default GroupsPage;
